import { useState } from "react";
import { NativeSelect, TextInput } from "@mantine/core";
import { Button } from "../ui/Button";

/* A person's phones and emails while editing: every one the lake has, each
 * with a Remove that can be undone before Save, the ones about to be added,
 * and a row to add one. Nothing is deleted: a removed value is marked wrong
 * by api.edit_record, with who and when, and stays out of every list. */

export type ContactPointRow = {
  id: string;
  person_id: string | null;
  kind: string | null;
  value: string | null;
  verification_status: string | null;
  created_at: string | null;
};

export type ReachDraft = { add: { kind: string; value: string }[]; remove: string[] };

export const KIND_WORDS: Record<string, string> = { mobile: "Mobile", phone: "Office line", email: "Email", office: "Office line" };

export function ReachEditor({ points, draft, onChange }: {
  points: readonly ContactPointRow[];
  draft: ReachDraft;
  onChange: (d: ReachDraft) => void;
}) {
  const [kind, setKind] = useState("mobile");
  const [value, setValue] = useState("");
  const canAdd = value.trim().length >= 5;
  const add = () => {
    if (!canAdd) return;
    onChange({ ...draft, add: [...draft.add, { kind, value: value.trim() }] });
    setValue("");
  };
  const toggleRemove = (id: string) => {
    onChange({
      ...draft,
      remove: draft.remove.includes(id) ? draft.remove.filter((x) => x !== id) : [...draft.remove, id],
    });
  };
  return (
    <div className="reach-editor">
      <ul className="reach-list">
        {points.map((p) => {
          const going = draft.remove.includes(p.id);
          return (
            <li key={p.id} className={going ? "reach-row reach-going" : "reach-row"}>
              <span className="reach-kind muted">{KIND_WORDS[p.kind ?? ""] ?? p.kind}</span>
              <span className="reach-value">{p.value}</span>
              {p.verification_status === "bounced" ? <span className="muted"> (bounced)</span> : null}
              <Button kind="link" size="compact-xs" className="reach-remove" onClick={() => toggleRemove(p.id)}>
                {going ? "Keep it" : "Remove"}
              </Button>
            </li>
          );
        })}
        {draft.add.map((a, i) => (
          <li key={`new-${i}`} className="reach-row reach-new">
            <span className="reach-kind muted">{KIND_WORDS[a.kind] ?? a.kind}</span>
            <span className="reach-value">{a.value}</span>
            <span className="muted"> (new)</span>
            <Button kind="link" size="compact-xs" className="reach-remove"
                    onClick={() => onChange({ ...draft, add: draft.add.filter((_, j) => j !== i) })}>
              Remove
            </Button>
          </li>
        ))}
      </ul>
      <div className="reach-add">
        <NativeSelect aria-label="Kind" value={kind} onChange={(e) => setKind(e.currentTarget.value)}
                      data={[{ value: "mobile", label: "Mobile" }, { value: "phone", label: "Office line" }, { value: "email", label: "Email" }]} />
        <TextInput aria-label={kind === "email" ? "Address" : "Number"} value={value} w="14rem"
                   onChange={(e) => setValue(e.currentTarget.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <Button size="compact-sm" onClick={add} disabled={!canAdd}>Add</Button>
      </div>
    </div>
  );
}
